import { ActivityType } from '@shared/models/design/activities/activity-type.model';
import { PROJECT_LABEL_NONE } from '@shared/models/project-label.model';
import { isProjectFolder } from '@shared/helpers/project-folder.helpers';
import { OneOfProjectListElements } from '@shared/models/project.model';

/**
 * Recursively replace folder `elements` with sorted, filtered children from {@link getChildren}.
 * Project rows are passed through. Used so the project list can read children from the tree
 * instead of calling {@link useSortedProjects} on every row.
 */
export const nestSortedProjectList = (
  nodes: OneOfProjectListElements[],
  getChildren: (parent: OneOfProjectListElements) => OneOfProjectListElements[],
): OneOfProjectListElements[] =>
  nodes.map((node) => {
    if (!isProjectFolder(node)) return node;
    return {
      ...node,
      elements: nestSortedProjectList(getChildren(node), getChildren),
    };
  });

/** Label matches are project-only; the shared list filter bubbles matching children to roots. */
export const matchesProjectLabelFilter = (
  item: OneOfProjectListElements,
  selectedIds: string[],
  assignments: Record<string, string[]>,
): boolean =>
  selectedIds.length === 0 ||
  (!isProjectFolder(item) &&
    selectedIds.some((id) =>
      id === PROJECT_LABEL_NONE
        ? !assignments[item.id]?.length
        : assignments[item.id]?.includes(id),
    ));

/** Match any selected program with positive GFA, including the donut's residual Other area. */
export const matchesProjectProgramFilter = (
  item: OneOfProjectListElements,
  selectedPrograms: string[],
): boolean => {
  if (!selectedPrograms.length) return true;
  if (isProjectFolder(item)) return false;
  const areas = item.program_areas ?? [];
  const unassignedArea = Math.max(
    0,
    (item.gfa ?? 0) - areas.reduce((sum, { area }) => sum + area, 0),
  );
  return selectedPrograms.some(
    (type) =>
      areas.some((program) => program.type === type && program.area > 0) ||
      (type === ActivityType.Other && unassignedArea > 0),
  );
};
